import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {apiRetrieveImproved} from "../../api";
import Content from "../../components/Content.tsx";
import {useJwtAuth} from "../../JWTAuthProvider.tsx";
import Header from "../../Header.tsx";
import '../../styles/player-dashboard.scss';

export const Route = createFileRoute('/_app/tier-journey')({
    component: RouteComponent,
})

// Badge + tier medal artwork, keyed by file basename (badge code / tier-{rank}).
const ART: Record<string, string> = Object.fromEntries(
    Object.entries(import.meta.glob('../../assets/badges/*.svg', {eager: true, query: '?url', import: 'default'}))
        .map(([path, url]) => [path.split('/').pop()!.replace('.svg', ''), url as string]),
);

type Badge = {
    code: string; name: string; family: string | null; tier: string; tier_points: number;
    coins: number; active: boolean; how: string | null; earned_at: string | null;
    progress: {current: number; target: number} | null;
};
type Rank = {key: string; name: string; threshold: number; perks: string[]};
type Journey = {
    enabled: boolean; tier_points: number; rank: Rank; next_rank: Rank | null; points_to_next: number;
    ranks: Rank[]; badges: Badge[]; earned_count: number; active_count: number; member_since: string | null;
};

const RING_COLORS: Record<string, string> = {
    rookie: '#3B74DC', bronze: '#C87F3F', silver: '#AEB9C6', gold: '#F0C64A', platinum: '#CFE3EE',
};

// Ladder families render in this order; everything without a family is a one-off badge.
const FAMILY_ORDER = ['Matches played', 'Wins', 'Win streak', 'Giant Slayer', 'Tournament Champion', 'League Champion', 'Play streak'];

const artFor = (code: string) => ART[code];

function BadgeCard({badge, size}: {badge: Badge; size: 'md' | 'sm'}) {
    const earned = !!badge.earned_at;
    return (
        <div className={'tj-badge' + (size === 'sm' ? ' sm' : '') + (earned ? '' : ' locked')} title={badge.how || badge.name}>
            <img src={artFor(badge.code)} alt={badge.name} />
            <div className="tj-badge-name">{badge.name}</div>
            <div className="tj-badge-meta">
                {earned
                    ? <>+{badge.tier_points} pts · {badge.earned_at}</>
                    : !badge.active
                        ? 'Coming soon'
                        : badge.progress
                            ? <>{badge.progress.current}/{badge.progress.target} · +{badge.tier_points} pts</>
                            : <>+{badge.tier_points} pts</>}
            </div>
        </div>
    );
}

function RouteComponent() {
    const user = useJwtAuth().user;
    const query = useQuery<Journey>({
        queryKey: ['tier-journey'],
        queryFn:  () => apiRetrieveImproved('player/tier-journey' as any, {}),
    });
    const j = query.data;

    if (!j) {
        return <><Header /><div className="flex justify-center py-16"><Spinner size="lg" /></div></>;
    }

    const rankIdx = j.ranks.findIndex((r) => r.key === j.rank.key);
    const next = j.next_rank;
    // Node centres sit at 10/30/50/70/90% — fill to the current node, then part-way to the next.
    const frac = next ? (j.tier_points - j.rank.threshold) / (next.threshold - j.rank.threshold) : 1;
    const fillPct = Math.min(90, 10 + rankIdx * 20 + Math.max(0, Math.min(1, frac)) * 20);

    const byFamily = new Map<string, Badge[]>();
    const oneOffs: Badge[] = [];
    for (const b of j.badges) {
        if (b.family) {
            byFamily.set(b.family, [...(byFamily.get(b.family) || []), b]);
        } else {
            oneOffs.push(b);
        }
    }
    // One-offs: earned first (gold pops at the top), then in-progress, then coming-soon.
    oneOffs.sort((a, b) => Number(!!b.earned_at) - Number(!!a.earned_at) || Number(b.active) - Number(a.active));

    const initial = (user?.name || '?').charAt(0).toUpperCase();

    return <>
        <Header />
        <Content className="max-w-[1500px] w-full px-4 pb-6">
            <div className="player-dash">

                <header className="pd-card tj-hero">
                    <div>
                        <p className="tj-eyebrow">SCOREPAL PLAYER PATH</p>
                        <h1 className="tj-title">Your Tier Journey</h1>
                        <p className="tj-lede">
                            Every badge you earn adds tier points. Points move you up the path — and each tier unlocks real perks.
                        </p>
                    </div>

                    {/* Player card */}
                    <div className="tj-player">
                        {user?.profile_thumb_url
                            ? <img src={user.profile_thumb_url} alt="" className="photo" style={{borderColor: RING_COLORS[j.rank.key]}} />
                            : <div className="photo" style={{borderColor: RING_COLORS[j.rank.key]}}>{initial}</div>}
                        <div>
                            <b>{user?.name} {user?.lastname}</b>
                            <span>{j.rank.name} tier{j.member_since ? <> · member since {j.member_since}</> : null}</span>
                        </div>
                        <div className="points">
                            <b>{j.tier_points}</b><br />
                            <span>tier points</span>
                        </div>
                    </div>
                </header>

                {/* Journey track */}
                <section className="pd-card tj-card">
                    <div className="tj-track-wrap">
                        <div className="tj-track"><i style={{width: `${fillPct}%`}} /></div>
                        <div className="tj-ranks">
                            {j.ranks.map((rank, i) => {
                                const state = i < rankIdx ? 'reached' : i === rankIdx ? 'current' : 'ahead';
                                return (
                                    <div key={rank.key} className={'tj-rank ' + state}>
                                        <div className="tj-medal"><img src={ART[`tier-${rank.key}`]} alt={rank.name} /></div>
                                        <div className="tj-rank-name">{rank.name}</div>
                                        <div className="tj-rank-pts">{rank.threshold} pts</div>
                                        <ul className="tj-perks">
                                            {rank.perks.map((perk) => <li key={perk}>{perk}</li>)}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {next && (
                        <div className="tj-next">
                            You're <b>{j.points_to_next} points</b> from <b>{next.name}</b> — keep earning badges to get there.
                        </div>
                    )}
                </section>

                {/* How points work */}
                <section className="pd-card tj-card">
                    <h2 className="pd-section-title">How points work</h2>
                    <p>
                        Each badge pays tier points by its metal. Points are permanent — spending coins on rewards never moves you back down the path.
                    </p>
                    <div className="tj-metals">
                        {([['Standard', 5, '#3B74DC'], ['Bronze', 10, '#C87F3F'], ['Silver', 20, '#AEB9C6'], ['Gold', 40, '#D4A017'], ['Platinum', 75, '#CFE3EE']] as const).map(([name, pts, color]) => (
                            <div key={name}>
                                <i style={{background: color}} />
                                <span>{name}</span>
                                <b>+{pts} pts</b>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Badge ladders */}
                <section className="pd-card tj-card">
                    <h2 className="pd-section-title">Badge ladders</h2>
                    <p style={{marginBottom: 0}}>
                        These families climb through the metals as you rack them up. Greyed badges are still waiting for you.
                    </p>
                    {FAMILY_ORDER.filter((f) => byFamily.has(f)).map((family) => (
                        <div key={family}>
                            <h3 className="tj-family">{family}</h3>
                            <div className="tj-badges">
                                {byFamily.get(family)!.map((b) => <BadgeCard key={b.code} badge={b} size="md" />)}
                            </div>
                        </div>
                    ))}
                </section>

                {/* One-off badges */}
                <section className="pd-card tj-card">
                    <h2 className="pd-section-title">One-off badges</h2>
                    <p>Single achievements that top up your points along the way.</p>
                    <div className="tj-badges">
                        {oneOffs.map((b) => <BadgeCard key={b.code} badge={b} size="sm" />)}
                    </div>
                </section>

                <footer className="tj-foot">
                    {j.earned_count} of {j.badges.length} badges earned · thresholds — Bronze {j.ranks[1]?.threshold}, Silver {j.ranks[2]?.threshold},
                    Gold {j.ranks[3]?.threshold}, Platinum {j.ranks[4]?.threshold} · badges marked "coming soon" unlock as new stats go live.
                </footer>
            </div>
        </Content>
    </>;
}
